// M13.3 — Mobile (vs non-mobile) section on /app/report. Sits directly
// after PlatformLeaderboard, before LandingPageTable in the dashboard
// scroll.
//
// Cohort = the same social-channel-group filter the rest of the report
// applies, so the device totals reconcile row-for-row against
// PlatformLeaderboard / TimeSeriesSection.
//
// Composition (top to bottom):
//   1. Section header — eyebrow + accent bar + title.
//   2. Donut (current period mix, ~140px square) on the right + 100%
//      stacked area (per-day trend) on the left, plus chips legend.
//      Stacks to a single column on small screens.
//   3. Per-device datatable: Device / Sessions / Active users /
//      Key events / Conversion rate / Revenue.

import { useMemo } from "react";
import type { ReportMobile, ReportMobilePerDeviceRow } from "@/lib/api";
import { SectionHeader } from "@/components/report/SectionHeader";
import { MobileAreaChart, MobileDonut } from "./MobileChart";
import { DEVICE_COLOR, DEVICE_LABEL, DEVICE_RENDER_ORDER, type DeviceKey } from "./device-colors";
import { formatCurrencyOrNumber } from "./format-currency";
import type { Granularity } from "./granularity";

export interface MobileSectionProps {
  mobile: ReportMobile;
  granularity: Granularity;
  /** Whether to render a Revenue column on the datatable. Mirrors the
   *  existing showRevenue gating used by PlatformLeaderboard so the
   *  table only carries currency cells when the property has any. */
  showRevenue: boolean;
  /** ISO 4217 from `meta.currencyCode`. Drives the Revenue column's
   *  symbol via the shared formatter — falls back to a locale-formatted
   *  plain number when the GA4 Admin API was unreachable. */
  currencyCode: string | null | undefined;
}

export function MobileSection({
  mobile,
  granularity,
  showRevenue,
  currencyCode,
}: MobileSectionProps) {
  // Order rows in the canonical Desktop / Mobile / Tablet / Other
  // sequence for the donut + legend; the datatable uses the same order
  // (rather than sorted-by-sessions) so multi-property comparisons read
  // consistently.
  const orderedPerDevice = useMemo(() => {
    const lookup = new Map(mobile.perDevice.map((r) => [r.device, r] as const));
    return DEVICE_RENDER_ORDER.flatMap<ReportMobilePerDeviceRow>((d) => {
      const row = lookup.get(d);
      return row ? [row] : [];
    });
  }, [mobile.perDevice]);

  const totalSessions = useMemo(
    () => mobile.perDevice.reduce((acc, r) => acc + r.sessions, 0),
    [mobile.perDevice],
  );

  const donutSlices = useMemo(
    () => mobile.perDevice.map((r) => ({ device: r.device, sessions: r.sessions })),
    [mobile.perDevice],
  );

  return (
    <section id="mobile" className="scroll-mt-24 space-y-4">
      <SectionHeader
        eyebrow="Devices"
        title={
          <>
            How <em className="font-serif text-purple-700">mobile</em> the social audience really is
          </>
        }
        description={
          <>
            Desktop, mobile and tablet sessions for the social cohort over the window. The donut
            shows the current-period mix at a glance; the stack tracks how the split moves day to
            day.
          </>
        }
      />

      <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_auto] md:items-start">
          <div className="min-w-0">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Trend
            </p>
            <MobileAreaChart
              rows={mobile.trend}
              granularity={granularity}
              ariaLabel="Device mix — 100% stacked area for the social cohort"
            />
          </div>
          <div className="flex flex-col items-center gap-3 md:items-end">
            <p className="self-start text-[11px] font-bold uppercase tracking-wide text-neutral-500 md:self-end">
              Current mix
            </p>
            <MobileDonut perDevice={donutSlices} ariaLabel="Device mix donut" />
            <DeviceLegend perDevice={mobile.perDevice} totalSessions={totalSessions} />
          </div>
        </div>
      </div>

      <DeviceDataTable
        rows={orderedPerDevice}
        totalSessions={totalSessions}
        showRevenue={showRevenue}
        currencyCode={currencyCode}
      />
    </section>
  );
}

interface DeviceLegendProps {
  perDevice: ReadonlyArray<{ device: DeviceKey; sessions: number }>;
  totalSessions: number;
}

function DeviceLegend({ perDevice, totalSessions }: DeviceLegendProps) {
  const lookup = new Map(perDevice.map((r) => [r.device, r.sessions] as const));
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1.5 md:flex-col md:items-end md:gap-y-2">
      {DEVICE_RENDER_ORDER.flatMap((d) => {
        const sessions = lookup.get(d) ?? 0;
        if (sessions === 0) return [];
        const share = totalSessions > 0 ? sessions / totalSessions : 0;
        return [
          <li
            key={d}
            className="inline-flex items-center gap-2 text-xs text-neutral-700 md:gap-2.5"
          >
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: DEVICE_COLOR[d] }}
            />
            <span className="font-semibold text-neutral-950">{DEVICE_LABEL[d]}</span>
            <span className="tabular-nums text-neutral-500">{(share * 100).toFixed(1)}%</span>
          </li>,
        ];
      })}
    </ul>
  );
}

interface DeviceDataTableProps {
  rows: ReadonlyArray<ReportMobilePerDeviceRow>;
  totalSessions: number;
  showRevenue: boolean;
  currencyCode: string | null | undefined;
}

function DeviceDataTable({ rows, totalSessions, showRevenue, currencyCode }: DeviceDataTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-5 text-sm text-neutral-500 ring-1 ring-neutral-950/5">
        No device data for this window.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-950/5">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left">
            <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Device
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Sessions
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Share
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Active users
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Key events
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Conv rate
            </th>
            {showRevenue ? (
              <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Revenue
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const share = totalSessions > 0 ? r.sessions / totalSessions : 0;
            return (
              <tr key={r.device} className="border-b border-neutral-100 last:border-b-0">
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: DEVICE_COLOR[r.device] }}
                    />
                    <span className="font-medium text-neutral-950">{DEVICE_LABEL[r.device]}</span>
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-950">
                  {formatNumber(r.sessions)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {(share * 100).toFixed(1)}%
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {formatNumber(r.activeUsers)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {formatNumber(r.keyEvents)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {(r.sessionKeyEventRate * 100).toFixed(2)}%
                </td>
                {showRevenue ? (
                  <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                    {formatCurrencyOrNumber(r.totalRevenue, currencyCode)}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat("en").format(Math.round(n));
}
